import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { CheckCircle, XCircle, Envelope, Phone, User } from "@phosphor-icons/react";

export default function Pending() {
  const [items, setItems] = useState([]);
  const [busyId, setBusyId] = useState(null);

  async function load() {
    try { const r = await api.get("/register/pending"); setItems(r.data); }
    catch (e) { toast.error(formatApiError(e)); }
  }
  useEffect(() => { load(); }, []);

  async function decide(id, action) {
    setBusyId(id);
    try {
      await api.post(`/register/pending/${id}/${action}`);
      toast.success(action === "approve" ? "Player approved" : "Registration rejected");
      window.dispatchEvent(new CustomEvent("wahdat:pending-updated"));
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusyId(null); }
  }

  return (
    <div className="space-y-6">
      <div>
        <div className="overline">Review</div>
        <h1 className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tight mt-1">Pending Registrations</h1>
        <div className="text-sm text-neutral-400 mt-2">Approvals add the player to your active roster. Rejections delete the request.</div>
      </div>

      {items.length === 0 ? (
        <div className="bg-[#121212] border border-neutral-800 rounded-lg p-10 text-center" data-testid="pending-empty">
          <div className="overline">Nothing to review</div>
          <div className="text-neutral-400 mt-2">Family sign-ups appear here as soon as they arrive.</div>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((p) => (
            <div key={p.id} data-testid={`pending-${p.id}`} className="bg-[#121212] border border-neutral-800 rounded-lg p-5 flex flex-col md:flex-row md:items-center gap-4">
              <div className="flex-1 min-w-0">
                <div className="font-display font-black text-xl uppercase tracking-tight">{p.first_name} {p.last_name}</div>
                <div className="overline mt-1">
                  {[p.position, p.age_group, p.team, p.preferred_foot && `${p.preferred_foot} foot`].filter(Boolean).join(" • ") || "—"}
                </div>
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm">
                  <span className="flex items-center gap-2 text-neutral-300"><User size={14} weight="bold" className="text-neutral-500" /> {p.guardian || "—"}</span>
                  <span className="flex items-center gap-2 text-neutral-300"><Envelope size={14} weight="bold" className="text-neutral-500" /> <a href={`mailto:${p.email}`} className="hover:text-white truncate">{p.email || "—"}</a></span>
                  <span className="flex items-center gap-2 text-neutral-300"><Phone size={14} weight="bold" className="text-neutral-500" /> {p.phone || "—"}</span>
                </div>
                {p.notes && <div className="text-sm text-neutral-400 mt-3 border-l-2 border-neutral-800 pl-3">{p.notes}</div>}
                <div className="overline mt-3">Received {new Date(p.created_at).toLocaleString()}</div>
              </div>
              <div className="flex gap-2 md:flex-col md:w-40">
                <button
                  onClick={() => decide(p.id, "approve")}
                  disabled={busyId === p.id}
                  data-testid={`approve-${p.id}`}
                  className="flex-1 h-11 px-4 rounded-md bg-[#06D6A0] hover:bg-[#04b587] text-[#050505] font-display font-bold uppercase tracking-wider text-xs inline-flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  <CheckCircle size={16} weight="fill" /> Approve
                </button>
                <button
                  onClick={() => decide(p.id, "reject")}
                  disabled={busyId === p.id}
                  data-testid={`reject-${p.id}`}
                  className="flex-1 h-11 px-4 rounded-md border border-[#D90429]/50 text-[#D90429] hover:bg-[#D90429]/10 font-display font-bold uppercase tracking-wider text-xs inline-flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  <XCircle size={16} weight="bold" /> Reject
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
